import React from "react";
import {
  Card, CardHeader, CardContent, Grid, Box, Avatar, Typography,
  Button, Chip, IconButton, Tooltip, useTheme
} from "@mui/material";
import AddPhotoAlternateRoundedIcon from "@mui/icons-material/AddPhotoAlternateRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import { useTranslation } from "react-i18next";

export default function GallerySection({
  formData,
  fileInputRef,
  handleClickAdd,
  handleDrop,
  handleDragOver,
  moveImage,
  makeCover,
  removeImage,
}) {
  const theme = useTheme();
  const { t } = useTranslation("products");

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardHeader
        title={t("form.galleryTitle")}
        sx={{ pb: 0.5, "& .MuiCardHeader-title": { fontSize: 16, fontWeight: 600 } }}
      />
      <CardContent sx={{ pt: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              sx={{
                border: `1px dashed ${theme.palette.divider}`,
                borderRadius: 2,
                p: 2,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
                background:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.03)"
                    : "rgba(0,0,0,0.02)",
              }}
            >
              <Avatar
                variant="rounded"
                sx={{ width: 56, height: 56, bgcolor: "action.hover" }}
              >
                <DragIndicatorRoundedIcon fontSize="small" />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2">{t("form.galleryDrop")}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("form.galleryOr")}
                  <Button size="small" variant="text" onClick={handleClickAdd} startIcon={<AddPhotoAlternateRoundedIcon />}>
                    {t("form.galleryChoose")}
                  </Button>
                </Typography>
              </Box>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => {
                  // handler fica no pai via handleClickAdd -> addFiles
                  const files = Array.from(e.target.files || []);
                  const ev = new CustomEvent("gallery:addFiles", { detail: files });
                  window.dispatchEvent(ev);
                  e.target.value = null;
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={1.5}>
              {(formData.galeria || []).map((img, idx) => {
                const src = img.url || img.preview;
                return (
                  <Grid item key={img.id || idx}>
                    <Box
                      sx={{
                        width: 120,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        overflow: "hidden",
                        position: "relative",
                        bgcolor: "background.paper",
                      }}
                    >
                      <Box
                        component="img"
                        src={src}
                        alt={t("form.imageAlt", { n: idx + 1 })}
                        sx={{ width: "100%", height: 90, objectFit: "cover" }}
                      />
                      <Box
                        sx={{
                          p: 0.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Chip size="small" label={`#${idx + 1}`} />
                        <Box>
                          <IconButton size="small" onClick={() => moveImage(idx, -1)} disabled={idx === 0}>
                            <ArrowBackIosNewRoundedIcon fontSize="inherit" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => moveImage(idx, +1)}
                            disabled={idx === (formData.galeria?.length || 0) - 1}
                          >
                            <ArrowForwardIosRoundedIcon fontSize="inherit" />
                          </IconButton>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          display: "flex",
                          gap: 0.5,
                        }}
                      >
                        <Tooltip
                          title={
                            img.isCover ? t("form.cover") : t("form.setCover")
                          }
                        >
                          <IconButton
                            size="small"
                            color={img.isCover ? "warning" : "default"}
                            onClick={() => makeCover(idx)}
                            sx={{
                              bgcolor: img.isCover ? "warning.light" : "background.paper",
                              "&:hover": {
                                bgcolor: img.isCover ? "warning.main" : "action.hover",
                              },
                            }}
                          >
                            {img.isCover ? (
                              <StarRoundedIcon fontSize="inherit" />
                            ) : (
                              <StarBorderRoundedIcon fontSize="inherit" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("form.remove")}>
                          <IconButton size="small" onClick={() => removeImage(idx)}>
                            <DeleteOutlineRoundedIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
